<template>
  <div class="tenancy-all-projects-wrapper">
    <sticky>
      <div class="clearfix header-panel">
        <div class="header">
          <span class="notice-header">
            {{ $i18n.t( 'tenantProjects.allTenantProjects.title') }}
          </span>
        </div>
        <div class="right-btns">
          <HKLButton
            color="orange"
            @click="exportExcel"
          >
            {{ $t('action.exportSearchResults') }}
          </HKLButton>
          <button
            type="button"
            class="btn btn--darkblue"
            role="reset-button"
            @click="resetFilters()"
          >
            {{ $t('action.resetFilter') }}
          </button>
          <button
            id="query-btn"
            type="button"
            class="btn btn--orange"
            role="search-button"
            @click="queryTenancyAllProjects()"
          >
            {{ $t('action.search') }}
          </button>
        </div>
      </div>
    </sticky>

    <div class="all-tenant-projects-table">
      <div style="margin-left: 15px;">
        <span>{{ `${$t('total')}: ${displayListLength}` }}</span>
      </div>
      <CommonTable
        ref="all-tenant-projects"
        :renderHeaderList="itemHeaders"
        tableId="all-tenant-projects"
        :needSearch="true"
        :yScrollAuto="true"
        :tableContentLists="sortedItems"
        :getDisplayListLength="getDisplayListLength"
        :defaultOrder="true"
        defaultOrderKey="date"
        :interactionFunc="interactionFunc"
        :getConditions="getConditions"
        :enterEvent="queryTenancyAllProjects"
        :orderByDateKeys="['date']"
        :orderMethods="orderMethods"
        :getTableOrder="getTableOrder"
      />
    </div>
    <HKLRemindModal ref="remindEmptyModal">
      <template slot="title">
        {{ $t('hint.emptySearch') }}
      </template>
    </HKLRemindModal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { ref } from 'vue-demi'
import { mapActions, mapGetters } from 'vuex'
import { getBuildingFloorZoneMap, getFloorIndex } from '@/api/buildings/request'
import { listAll as listAllHotbs } from '@/api/hotb/request'
import { exportAllTenantProjects, listAll as listAllTenantProjects } from '@/api/tenantProjects/request'
import {
  consumeResumeSearchQueryOnce,
  TENANT_PROJECTS_VIEW_ALL_LEAVE_PRESERVE_ROUTE_NAMES,
} from '@/constants/viewAllTableCache'
import HKLButton from '@/controls/Button/HKLButton'
import CommonTable from '@/controls/CommonTable'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLRemindModal from '@/controls/Modal/HKLRemindModal.vue'
import { useRefModal } from '@/controls/Modal/modal.hook'
import viewAllCommonTableSnapshotMixin from '@/mixins/viewAllCommonTableSnapshot.mixin'
import { INPUT_TYPE } from '@/modules/contacts/searchFilter.const'
import { useStoreActions, useStoreGetters } from '@/store'
import { DATA_CACHE_KEYS } from '@/store/util'
import { aclHelper, tmsPermsTypes } from '@/utils/aclHelper'
import dateUtil from '@/utils/dateUtil'
import { CREATOR_TYPES, hotbStatus, hotbTypes, tenantProjectStatus, tenantProjectTypes } from '@/utils/locale'
import { getHotbCompanyName, getHotbPremises, getHotbTradeName } from '../hotb/utils'
import { useGreenFitoutTableHeader } from './AllTenantProjects.hook'
import { allTenantProjectsMixin, normalizeSelectFilterValue } from './allTenantProjectsMixin'
import { useUserBuildingOpts } from './hook'

const sortingAttributes = {
  project: {
    status: null,
    type: null,
    projectType: null,
    projectLocation: null,
    projectName: null,
    floor: null,
  },
  tenancy: {
    tradeName: null,
    building: null,
  },
  company: {
    name: null,
  },
  statusChangedStartDay: null,
  statusChangedEndDay: null,
  greenFitout: {
    status: undefined,
  },
}

export default {
  name: 'AllTenantProjects',
  /** @see `@/mixins/viewAllCommonTableSnapshot.mixin.js` */
  viewAllTableSnapshotId: 'all-tenant-projects',
  viewAllTableLeavePreserveRouteNames: TENANT_PROJECTS_VIEW_ALL_LEAVE_PRESERVE_ROUTE_NAMES,
  components: {
    CommonTable,
    HKLButton,
    HKLRemindModal,
  },
  mixins: [allTenantProjectsMixin, viewAllCommonTableSnapshotMixin],
  setup () {
    let returnData = {}
    const greenFitoutTableHeader = useGreenFitoutTableHeader()

    const defaultOrderKey = 'date'
    const hotbCacheKey = DATA_CACHE_KEYS.VIEW_ALL_HOTB
    const projectCacheKey = DATA_CACHE_KEYS.VIEW_ALL_TP
    const orderKey = ref('date')
    const reverse = ref(true)
    const getTableOrder = (key, order) => {
      orderKey.value = key
      reverse.value = order
    }
    const { lastSearchKeys } = useStoreGetters('tenantProject', ['lastSearchKeys'])
    const { setLastSearchKeys } = useStoreActions('tenantProject', ['setLastSearchKeys'])
    const exportExcel = async () => {
      await exportAllTenantProjects({
        ...lastSearchKeys.value,
        orderInfo: {
          orderbyAttr: orderKey.value,
          reverse: Number(reverse.value),
        },
      })
    }
    returnData = Object.assign(returnData, {
      exportExcel,
      defaultOrderKey,
      getTableOrder,
      hotbCacheKey,
      projectCacheKey,
      lastSearchKeys,
      setLastSearchKeys,
      greenFitoutTableHeader,
    })
    const { open: remindEmptyModalOpen, modal: remindEmptyModal } = useRefModal()
    const { buildingOptions } = useUserBuildingOpts()
    returnData = Object.assign(returnData, {
      remindEmptyModalOpen,
      remindEmptyModal,
      buildingOptions,
    })
    return returnData
  },
  data () {
    const tenantProjectStatusOptions = Object.keys(tenantProjectStatus).map((status) => ({ label: status, val: status }))
    return {
      dateUtil,
      sortingAttributes: lodash.cloneDeep(sortingAttributes),
      tenantProjectStatusOptions,
      buildings: [],
      buildingFloorZoneMap: [],
      emptyValue: this.$t('emptyValue'),
      interactionFunc: {
        projectLocation: this.navToDetail,
      },
      orderMethods: {
        floor: this.orderByFloor,
      },
      /** View-all row payloads (not Vuex); refetched on each search / remount auto-query */
      hotbRows: [],
      tpRows: [],
      displayListLength: 0,
    }
  },
  computed: {
    ...mapGetters('tenantProject', ['lastSearchedAttributesAll']),
    ...mapGetters('user', ['currentUser', 'isTenant']),
    floorOptions () {
      if (this.sortingAttributes.tenancy.building && this.buildingFloorZoneMap[this.sortingAttributes.tenancy.building]) {
        return this.buildingFloorZoneMap[this.sortingAttributes.tenancy.building].floor
      }
      return []
    },
    typeSelectOptions () {
      return this.typeOptions.map((opt) => ({
        name: this.$options.filters.customTProjectTypeLabel(opt.label),
        value: opt.val,
      }))
    },
    statusSelectOptions () {
      return this.statusOptions.map((opt) => ({
        name: this.$options.filters.customStatusLabel(opt.label),
        value: opt.val,
      }))
    },
    buildingSelectOptions () {
      return (this.buildingOptions || []).map((code) => ({ name: code, value: code }))
    },
    floorSelectOptions () {
      return (this.floorOptions || []).map((floor) => ({ name: floor, value: floor }))
    },
    isFloorFilterDisabled () {
      return !this.sortingAttributes.tenancy.building || !this.floorSelectOptions.length
    },
    isBuildingFilterDisabled () {
      return !this.buildingSelectOptions.length
    },
    canCreateUpdateProject () {
      return aclHelper.getAccess(tmsPermsTypes.project.createUpdate)
    },
    canReadProjects () {
      return aclHelper.getAccess(tmsPermsTypes.project.read)
    },
    canAccessHotbs () {
      return aclHelper.getAccess(tmsPermsTypes.hotb.read)
    },

    parsedAllHotbs () {
      return (this.hotbRows || []).map((rec) => {
        return {
          id: rec.id,
          status: rec.status,
          type: this.$options.filters.capitalize(rec.type),
          tradeName: getHotbTradeName(rec),
          building: rec.tenancy.building.code,
          companyName: getHotbCompanyName(rec),
          date: rec.scheduledTransactionDate || rec.transactionDate,
          projectLocation: getHotbPremises(rec),
          projectName: rec.name,
          floors: rec.floor,
          allowLink: this.enableHotbLink(rec),
        }
      })
    },
    parsedAllTenantProjects () {
      return (this.tpRows || []).map((rec) => {
        const projectLocation = rec.projectLocation.join(', ') + (rec.description ? ` - ${rec.description}` : '')
        return {
          ...this.mapItem(rec),
          date: rec.statusUpdatedAt,
          projectLocation,
          projectName: rec.projectName,
          floors: rec.floors,
          allowLink: this.enableTenantProjectLink(rec),
          greenFitoutStatus: this.greenFitoutTableHeader.parserGreenFitoutStatusByProject(rec),
        }
      })
    },
    allProjects () {
      return [].concat(this.parsedAllHotbs).concat(this.parsedAllTenantProjects)
        .map(rec => {
          rec.floors = lodash.sortBy(rec.floors, item => {
            return getFloorIndex(rec.building, item)
          })
          rec.projectLocation = rec.projectLocation ? rec.projectLocation : null
          rec.floor = rec.floors.join(', ')
          rec.floorIndex = getFloorIndex(rec.building, rec.floors[0])
          rec.greenFitoutStatus = rec.greenFitoutStatus || null
          return rec
        })
    },
    sortedItems () {
      const result = lodash.orderBy(this.allProjects, (obj) => {
        const millis = obj.date ? new Date(obj.date).getTime() : 0
        if (this.orderbyAttr === 'date') {
          return millis
        } else if (this.orderbyAttr === 'floor') {
          return getFloorIndex(obj.building, obj.floors[0])
        } else {
          return obj[this.orderbyAttr]
        }
      })
      return this.reverse ? result.reverse() : result
    },
    itemHeaders () {
      return [{
        name: 'type',
        title: this.$t('type'),
        queryName: ['project', 'type'],
        component: HKLSelect,
        options: {
          attributes: {
            options: this.typeSelectOptions,
            showSearch: true,
            placeholder: '',
          },
        },
        formatRule: val => this.$options.filters.customTProjectTypeLabel(val),
      }, {
        name: 'companyName',
        title: this.$t('tenantProjects.allTenantProjects.company'),
        queryName: ['company', 'name'],
        inputType: INPUT_TYPE.INPUT,
      }, {
        name: 'tradeName',
        title: this.$t('tenantProjects.allTenantProjects.tradeName'),
        queryName: ['tenancy', 'tradeName'],
        inputType: INPUT_TYPE.INPUT,
      }, {
        name: 'building',
        title: this.$t('tenantProjects.allTenantProjects.building'),
        queryName: ['tenancy', 'building'],
        disabledSearch: this.isBuildingFilterDisabled,
        component: HKLSelect,
        options: {
          attributes: {
            options: this.buildingSelectOptions,
            showSearch: true,
            placeholder: '',
          },
        },
      }, {
        name: 'floor',
        title: this.$t('tenantProjects.allTenantProjects.floor'),
        queryName: ['project', 'floor'],
        disabledSearch: this.isFloorFilterDisabled,
        component: HKLSelect,
        options: {
          attributes: {
            disabled: this.isFloorFilterDisabled,
            options: this.floorSelectOptions,
            showSearch: true,
            placeholder: '',
          },
        },
        defaultValue: this.emptyValue,
      }, {
        name: 'projectLocation',
        title: this.$t('tenantProjects.allTenantProjects.premises'),
        queryName: ['project', 'projectLocation'],
        inputType: INPUT_TYPE.INPUT,
        defaultValue: this.emptyValue,
      }, {
        name: 'status',
        title: this.$t('tenantProjects.allTenantProjects.status'),
        queryName: ['project', 'status'],
        component: HKLSelect,
        options: {
          attributes: {
            options: this.statusSelectOptions,
            showSearch: true,
            placeholder: '',
          },
        },
        formatRule: val => this.$options.filters.customStatusLabel(val),
      }, {
        name: 'date',
        title: this.$t('statusDate'),
        datePickerCanPutTotop: false,
        inputType: 'date',
      },
      this.greenFitoutTableHeader.headerItem,
      ].filter(Boolean)
    },
  },
  async mounted () {
    const { BuildingFloorZoneMap } = await getBuildingFloorZoneMap()
    this.buildingFloorZoneMap = BuildingFloorZoneMap
    $('#search-panel').keydown(function (event) {
      var keypressed = event.keyCode || event.which
      if (keypressed === 13) {
        $('#query-btn').click()
      }
    })
    this.setStatusOptions()
    $('.all-tenant-projects-table-content-container').css('width', $('.all-tenant-projects-table-header-container tr').width())
    const { didStripResumeSearch } = await consumeResumeSearchQueryOnce(
      this.$router,
      this.$route.query,
      'allTenantProjects',
    )
    const lastSearchAttributes = this.lastSearchedAttributesAll
    if (didStripResumeSearch && Object.keys(lastSearchAttributes).length > 0) {
      this.sortingAttributes = lastSearchAttributes
    }
    await this.$nextTick()
    this.syncTableFilterConditionsFromSortingAttributes()
    if (didStripResumeSearch && !this.checkIsEmptyFilter()) {
      await this.queryTenancyAllProjects()
    }
  },
  methods: {
    ...mapActions('tenantProject', ['setLastSearchedAttributesAll']),
    getDisplayListLength (length) {
      this.displayListLength = length
    },
    resetFilters () {
      this.sortingAttributes = lodash.cloneDeep(sortingAttributes)
      this.setLastSearchedAttributesAll({})
      this.$nextTick(() => {
        this.syncTableFilterConditionsFromSortingAttributes()
        const table = this.$refs['all-tenant-projects']
        if (table) table.resetFilterUiOnly()
      })
    },
    /** Clear local lists before each search (partial API skips must not leave stale rows) */
    clearLastSearchAttrsResult () {
      this.setLastSearchedAttributesAll(this.sortingAttributes)
      this.hotbRows = []
      this.tpRows = []
      this.setLastSearchKeys({})
    },
    loadViewAll (key) {
      if (this.checkIsEmptyFilter()) return this.remindEmptyModalOpen()
      const [hotbParsedQueryParams, projectParsedQueryParams] = this.getParsedAttributes()
      const map = {
        async [this.hotbCacheKey] () {
          if (!(this.canAccessHotbs && !Object.keys(tenantProjectTypes).includes(hotbParsedQueryParams.hotb.type))) return
          // The HOTB does not have the green fit-out
          if (projectParsedQueryParams.greenFitout.status) return
          const hotbs = await listAllHotbs({ ...hotbParsedQueryParams }, this.hotbCacheKey)
          this.hotbRows = Array.isArray(hotbs) ? hotbs : []
          return { hotbCacheKey: this.hotbCacheKey }
        },
        async [this.projectCacheKey] () {
          if (!(this.canReadProjects && !Object.keys(hotbTypes).includes(hotbParsedQueryParams.hotb.type))) return
          const projects = await listAllTenantProjects({ ...projectParsedQueryParams }, this.projectCacheKey)
          this.tpRows = Array.isArray(projects) ? projects : []
          return { projectCacheKey: this.projectCacheKey }
        },
      }
      return map[key].bind(this)()
    },
    applyFilterConditionsToSortingAttributes (rawConditions) {
      const conditions = lodash.cloneDeep(rawConditions || {})
      const buildingValue = normalizeSelectFilterValue(lodash.get(conditions, 'building.value'))
      if (buildingValue === undefined || buildingValue === null || buildingValue === '') {
        delete conditions.building
        delete conditions.floor
        this.sortingAttributes.tenancy.building = null
        this.sortingAttributes.project.floor = null
      } else {
        const floorValue = normalizeSelectFilterValue(lodash.get(conditions, 'floor.value'))
        const floors = lodash.get(this.buildingFloorZoneMap, [buildingValue, 'floor'], [])
        const hasFloorFilter = floorValue !== undefined && floorValue !== null && floorValue !== ''
        if (!hasFloorFilter || !floors.includes(floorValue)) {
          delete conditions.floor
          this.sortingAttributes.project.floor = null
        }
      }
      this.searchConditionsConvertToQuery(conditions)
    },
    async queryTenancyAllProjects () {
      const table = this.$refs['all-tenant-projects']
      if (table && table.filterConditions) {
        this.applyFilterConditionsToSortingAttributes(table.filterConditions)
      }
      this.clearLastSearchAttrsResult()
      try {
        this.$root.$children[0].$refs.loadingLayer.$emit('startLoading')
        const results = await Promise.all([this.loadViewAll(this.hotbCacheKey), this.loadViewAll(this.projectCacheKey)])
        this.setLastSearchKeys(Object.assign({}, ...results.filter(Boolean)))
      } finally {
        this.$root.$children[0].$refs.loadingLayer.$emit('stopLoading')
      }
    },
    showProjectLocation (item) {
      if (Array.isArray(item.projectLocation)) {
        return item.projectLocation ? item.projectLocation.join(', ') : this.emptyValue
      } else {
        return item.projectLocation ? item.projectLocation : this.emptyValue
      }
    },
    getParsedAttributes () {
      let sortingAttributes = lodash.cloneDeep(this.sortingAttributes)
      sortingAttributes.project.type = normalizeSelectFilterValue(sortingAttributes.project.type)
      sortingAttributes.project.status = normalizeSelectFilterValue(sortingAttributes.project.status)
      if (sortingAttributes.greenFitout.status != null) {
        sortingAttributes.greenFitout.status = normalizeSelectFilterValue(sortingAttributes.greenFitout.status)
      }

      if (this.sortingAttributes.statusChangedEndDay) {
        sortingAttributes.statusChangedEndDay = new Date(Date.parse(sortingAttributes.statusChangedEndDay) + dateUtil.ONE_DAY_MILLI - 1)
      }
      const buildingId = sortingAttributes.tenancy.building ? this.buildingFloorZoneMap[sortingAttributes.tenancy.building].id : null

      const hotbParsedQueryParams = {
        hotb: {
          status: sortingAttributes.project.status || '',
          type: sortingAttributes.project.type || '',
          premises: sortingAttributes.project.projectLocation,
          name: sortingAttributes.project.projectName,
          floor: sortingAttributes.project.floor,
          tradeName: sortingAttributes.tenancy.tradeName,
          companyName: sortingAttributes.company.name,
          date: {
            startDay: sortingAttributes.statusChangedStartDay,
            endDay: sortingAttributes.statusChangedEndDay,
          },
        },
        tenancy: {
          buildingId: buildingId,
        },
      }
      const projectParsedQueryParams = {
        project: {
          status: sortingAttributes.project.status || '',
          type: sortingAttributes.project.type || '',
          projectLocation: sortingAttributes.project.projectLocation,
          projectName: sortingAttributes.project.projectName,
          floors: sortingAttributes.project.floor,
          description: sortingAttributes.project.projectLocation,
        },
        tenancy: {
          tradeName: sortingAttributes.tenancy.tradeName,
          buildingId: buildingId,
        },
        company: {
          name: sortingAttributes.company.name,
        },
        statusChangedStartDay: sortingAttributes.statusChangedStartDay,
        statusChangedEndDay: sortingAttributes.statusChangedEndDay,
        greenFitout: {
          status: sortingAttributes.greenFitout.status,
        },
      }
      return [hotbParsedQueryParams, projectParsedQueryParams]
    },
    syncTableFilterConditionsFromSortingAttributes () {
      const table = this.$refs['all-tenant-projects']
      if (!table) return
      const conditions = {}
      const setComponentFilter = (name, value) => {
        if (value === undefined || value === null) return
        if ((name === 'building' || name === 'floor') && value === '') return
        conditions[name] = {
          value,
          inputType: INPUT_TYPE.COMPONENT,
        }
      }
      setComponentFilter('type', lodash.get(this.sortingAttributes, 'project.type'))
      setComponentFilter('status', lodash.get(this.sortingAttributes, 'project.status'))
      setComponentFilter('building', lodash.get(this.sortingAttributes, 'tenancy.building'))
      setComponentFilter('floor', lodash.get(this.sortingAttributes, 'project.floor'))
      const gfStatus = lodash.get(this.sortingAttributes, 'greenFitout.status')
      if (gfStatus !== undefined && gfStatus !== null) {
        setComponentFilter(this.greenFitoutTableHeader.headerName, gfStatus)
      }
      if (this.sortingAttributes.statusChangedStartDay || this.sortingAttributes.statusChangedEndDay) {
        conditions.date = {
          value: [
            this.sortingAttributes.statusChangedStartDay || null,
            this.sortingAttributes.statusChangedEndDay || null,
          ],
          inputType: INPUT_TYPE.DATE,
        }
      }
      const managedKeys = [
        'type',
        'status',
        'building',
        'floor',
        'date',
        this.greenFitoutTableHeader.headerName,
      ].filter(Boolean)
      const next = lodash.cloneDeep(table.filterConditions || {})
      managedKeys.forEach((key) => {
        delete next[key]
      })
      table.filterConditions = Object.assign(next, conditions)
    },
    mapItem (rec) {
      return {
        id: rec.id,
        status: rec.status,
        type: this.$options.filters.capitalize(rec.type),
        tradeName: rec.tenancy.tradeName,
        building: rec.tenancy.building.code,
        companyName: lodash.get(rec, 'tenancy.company.name'),
      }
    },
    enableHotbLink (hotb) {
      const isPIC = hotb.managedBy === this.currentUser.id
      const isDraft = hotbStatus.DRAFT === hotb.status
      const hasCreateHotbAccess = isDraft && aclHelper.getAccess(tmsPermsTypes.hotb.create)
      const validStatusByPIC = [hotbStatus.DRAFT, hotbStatus.WAPPR, hotbStatus.REJECTED].includes(hotb.status) ? isPIC : true
      const isWaitingForAppr = (hotbStatus.WAPPR === hotb.status && aclHelper.getAccess(tmsPermsTypes.hotb.approve))
      return validStatusByPIC || hasCreateHotbAccess || isWaitingForAppr
    },
    enableTenantProjectLink (rec) {
      const enableDraftItemLInk = rec.status === tenantProjectStatus.DRAFT && rec.creatorType === CREATOR_TYPES.ENGINEER && this.canCreateUpdateProject
      return rec.status !== tenantProjectStatus.DRAFT || enableDraftItemLInk
    },
    getConditions (rawConditions) {
      this.applyFilterConditionsToSortingAttributes(rawConditions)
      this.$nextTick(() => this.syncTableFilterConditionsFromSortingAttributes())
    },
    searchConditionsConvertToQuery (conditons) {
      this.itemHeaders.map(header => {
        const condition = conditons[header.name]
        if (condition) {
          const value = typeof condition.value === 'string' ? condition.value.trim() : condition.value
          switch (header.name) {
            case 'date':
              this.sortingAttributes.statusChangedStartDay = value[0] || null
              this.sortingAttributes.statusChangedEndDay = value[1] || null
              break
            default:
              this.sortingAttributes[header.queryName[0]][header.queryName[1]] = value
              break
          }
        }
      })
    },
    orderByFloor (item, order, orderKey) {
      if (item.floor) {
        return item.floorIndex
      } else {
        return order ? item.floorIndex : Number.MIN_SAFE_INTEGER
      }
    },
  },
}
</script>

<style lang="scss" scoped src="styles/viewAll.scss"></style>
<style lang="scss" scoped src="styles/dashboard/style.scss"></style>
<style lang="scss" scoped src="styles/dashboard/datatable.scss"></style>
<style lang="scss" scoped>
.all-tenant-projects-table {
  height: calc(100vh - 196px);
}
</style>

<style lang="scss">
$BUILDING_COLUMN_WIDTH: 130px;
$FLOOR_COLUMN_WIDTH: $BUILDING_COLUMN_WIDTH;
$TYPE_COLUMN_WIDTH: 185px;
$STATUS_COLUMN_WIDTH: 180px;
$COMPANY_NAME_COLUMN_WIDTH: 300px;
$TRADE_NAME_COLUMN_WIDTH: $COMPANY_NAME_COLUMN_WIDTH;
$PROJECT_LOCATION_COLUMN_WIDTH: 270px;
$DATE_COLUMN_WIDTH: 330px;
$GF_STATUS_COLUMN_WIDTH: 235px;

$WIDTH_WITHOUT_PROJECT_LOCATION: $BUILDING_COLUMN_WIDTH
  + $FLOOR_COLUMN_WIDTH
  + $TYPE_COLUMN_WIDTH
  + $STATUS_COLUMN_WIDTH
  + $COMPANY_NAME_COLUMN_WIDTH
  + $TRADE_NAME_COLUMN_WIDTH
  + $DATE_COLUMN_WIDTH
  + $GF_STATUS_COLUMN_WIDTH;

$TABLE_WIDTH: $WIDTH_WITHOUT_PROJECT_LOCATION + $PROJECT_LOCATION_COLUMN_WIDTH + 25px;

.all-tenant-projects_common-table {
  height: calc(100% - 35px);
  .common-table-header-container {
    min-width: $TABLE_WIDTH - $GF_STATUS_COLUMN_WIDTH !important;
  }
  .building {
    min-width: $BUILDING_COLUMN_WIDTH;
    max-width: $BUILDING_COLUMN_WIDTH;
  }
  .floor{
    min-width: $FLOOR_COLUMN_WIDTH;
    max-width: $FLOOR_COLUMN_WIDTH;
  }
  .type {
    min-width: $TYPE_COLUMN_WIDTH;
    max-width: $TYPE_COLUMN_WIDTH;
  }
  .status {
    min-width: $STATUS_COLUMN_WIDTH;
    max-width: $STATUS_COLUMN_WIDTH;
  }
  .companyName {
    min-width: $COMPANY_NAME_COLUMN_WIDTH;
    max-width: $COMPANY_NAME_COLUMN_WIDTH;
  }
  .tradeName {
    min-width: $TRADE_NAME_COLUMN_WIDTH;
    max-width: $TRADE_NAME_COLUMN_WIDTH;
  }
  .projectLocation {
    min-width: $PROJECT_LOCATION_COLUMN_WIDTH;
    width: calc(100% - #{$WIDTH_WITHOUT_PROJECT_LOCATION - $GF_STATUS_COLUMN_WIDTH})
  }
  .date {
    min-width: $DATE_COLUMN_WIDTH;
    max-width: $DATE_COLUMN_WIDTH;
  }
  .greenFitoutStatus {
    min-width: $GF_STATUS_COLUMN_WIDTH;
    max-width: $GF_STATUS_COLUMN_WIDTH;
  }
  /* PMS-157 CommonTable filter th { min-width: 0 } overrides column widths here; restore for filter row. */
  .common-header-filter {
    th {
      font-weight: 400;

      label {
        font-weight: 400;
      }
    }
    th.type {
      min-width: $TYPE_COLUMN_WIDTH;
      max-width: $TYPE_COLUMN_WIDTH;
    }
    th.building {
      min-width: $BUILDING_COLUMN_WIDTH;
      max-width: $BUILDING_COLUMN_WIDTH;
    }
    th.floor {
      min-width: $FLOOR_COLUMN_WIDTH;
      max-width: $FLOOR_COLUMN_WIDTH;
    }
    th.status {
      min-width: $STATUS_COLUMN_WIDTH;
      max-width: $STATUS_COLUMN_WIDTH;
    }
    th.companyName {
      min-width: $COMPANY_NAME_COLUMN_WIDTH;
      max-width: $COMPANY_NAME_COLUMN_WIDTH;
    }
    th.tradeName {
      min-width: $TRADE_NAME_COLUMN_WIDTH;
      max-width: $TRADE_NAME_COLUMN_WIDTH;
    }
    th.projectLocation {
      min-width: $PROJECT_LOCATION_COLUMN_WIDTH;
    }
    th.date {
      min-width: $DATE_COLUMN_WIDTH;
      max-width: $DATE_COLUMN_WIDTH;
    }
    th.greenFitoutStatus {
      min-width: $GF_STATUS_COLUMN_WIDTH;
      max-width: $GF_STATUS_COLUMN_WIDTH;
    }
  }
  &:has(.greenFitoutStatus) {
    .common-table-header-container {
      min-width: $TABLE_WIDTH !important;
    }
    .projectLocation {
      width: calc(100% - #{$WIDTH_WITHOUT_PROJECT_LOCATION})
    }
  }
}
</style>
